/*
  Summit Feedback form for one workshop/session.
  Free-text questions; Save Progress (draft) or Submit; answers stay editable
  after submit until the summit year is captured.
  Write-in ("Other") mode: workshopId is "other" for a new entry (user provides
  topic + speaker) or resolves to the user's own write-in response when editing.
  Write-ins only answer the workshop-specific questions (skipForOther filtered).
*/
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get, post, getErrorMessage } from '../api/client';

const textInputStyle = {
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  padding: '0.5rem 0.65rem',
  borderRadius: '8px',
  border: '1px solid var(--cr-border)',
  fontSize: '1rem',
  fontFamily: 'inherit',
};

const SummitFeedbackFormPage = () => {
  const { yearId, workshopId } = useParams();
  const navigate = useNavigate();
  const isNewOther = workshopId === 'other';

  const [template, setTemplate] = useState(null);
  const [workshop, setWorkshop] = useState(null);
  const [isOtherEntry, setIsOtherEntry] = useState(isNewOther);
  const [topic, setTopic] = useState('');
  const [speaker, setSpeaker] = useState('');
  const [yearStatus, setYearStatus] = useState('open');
  const [yearTitle, setYearTitle] = useState('');
  const [responseStatus, setResponseStatus] = useState('draft');
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError('');
      try {
        if (isNewOther) {
          const [tRes, cRes] = await Promise.all([
            get('/api/summit-feedback/template'),
            get('/api/summit-feedback/current'),
          ]);
          if (tRes.ok) setTemplate(tRes.data);
          if (cRes.ok && cRes.data?.year && String(cRes.data.year._id) === String(yearId)) {
            setYearStatus(cRes.data.year.status);
            setYearTitle(cRes.data.year.title);
            setWorkshop({ isOther: true });
            setIsOtherEntry(true);
          } else {
            setError('This summit year is not open for feedback.');
          }
        } else {
          const [tRes, rRes] = await Promise.all([
            get('/api/summit-feedback/template'),
            get(`/api/summit-feedback/years/${yearId}/workshops/${workshopId}/my-response`),
          ]);
          if (tRes.ok) setTemplate(tRes.data);
          if (rRes.ok) {
            setWorkshop(rRes.data.workshop);
            setYearStatus(rRes.data.yearStatus);
            setYearTitle(rRes.data.yearTitle);
            setResponseStatus(rRes.data.response?.status || 'draft');
            setAnswers(rRes.data.response?.answers || {});
            if (rRes.data.workshop?.isOther || rRes.data.response?.isOther) {
              setIsOtherEntry(true);
              setTopic(rRes.data.response?.otherTopic || rRes.data.workshop?.name || '');
              setSpeaker(rRes.data.response?.otherSpeaker || rRes.data.workshop?.speaker || '');
            }
          } else {
            setError(getErrorMessage(rRes, 'Failed to load feedback form'));
          }
        }
      } catch (err) {
        console.error(err);
        setError('Failed to load feedback form');
      } finally {
        setLoading(false);
      }
    };
    if (yearId && workshopId) fetch();
  }, [yearId, workshopId, isNewOther]);

  const locked = yearStatus === 'captured';
  const questions = (template?.questions || []).filter((q) => (isOtherEntry ? !q.skipForOther : true));

  const updateAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setSavedAt(null);
  };

  const save = async (submit) => {
    if (isOtherEntry && !topic.trim()) {
      setError('Please enter the workshop or session topic.');
      return false;
    }
    setSaving(true);
    setError('');

    const body = isOtherEntry ? { answers, submit, topic: topic.trim(), speaker: speaker.trim() } : { answers, submit };
    const url = isNewOther
      ? `/api/summit-feedback/years/${yearId}/other-response`
      : `/api/summit-feedback/years/${yearId}/workshops/${workshopId}/response`;

    const { ok, status, data } = await post(url, body);
    setSaving(false);
    if (!ok) {
      setError(getErrorMessage({ status, data }, submit ? 'Failed to submit' : 'Failed to save'));
      return false;
    }
    setResponseStatus(data?.status || (submit ? 'submitted' : 'draft'));
    setSavedAt(new Date());
    if (isNewOther && !submit && data?.workshopId) {
      // Draft created: switch the URL to the real entry id so further saves update it
      navigate(`/summit-feedback/${yearId}/workshop/${data.workshopId}`, { replace: true });
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await save(true);
    if (ok) navigate('/summit-feedback', { state: { submitted: true } });
  };

  if (loading) return <div style={{ padding: '2rem' }}>Loading…</div>;
  if ((!workshop || !template) || (isNewOther && error)) {
    return (
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 1rem' }}>
        <PageHeader title="Summit Feedback" backTo="/summit-feedback" backLabel="← Summit Feedback" />
        <p>{error || 'Workshop not found.'}</p>
      </div>
    );
  }

  const title = isNewOther
    ? 'Other workshop or session'
    : isOtherEntry
      ? topic || 'Other workshop or session'
      : `Session ${workshop.sessionNumber}: ${workshop.name}`;

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 1rem 6rem 1rem' }}>
      <PageHeader title={title} backTo="/summit-feedback" backLabel="← Summit Feedback" />
      <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.9rem', margin: '0 0 1rem' }}>
        {[yearTitle, isOtherEntry ? 'Write-in session' : workshop.speaker].filter(Boolean).join(' · ')}
      </p>

      {locked && (
        <p style={{ background: 'var(--cr-bg-warm)', border: '1px solid var(--cr-border)', borderRadius: '8px', padding: '0.75rem 1rem', fontWeight: 500 }}>
          This summit year has been captured, so responses are locked. You can still read your answers below.
        </p>
      )}
      {!locked && !isNewOther && responseStatus === 'submitted' && (
        <p style={{ color: 'var(--cr-text)', marginBottom: '1rem', fontWeight: 500 }}>
          Submitted — you can still edit your answers until feedback is captured.
        </p>
      )}

      <form onSubmit={handleSubmit}>
        {isOtherEntry && (
          <div style={{ background: 'var(--cr-bg-warm)', border: '1px solid var(--cr-border)', borderRadius: '10px', padding: '1rem 1.15rem', marginBottom: '1.5rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label htmlFor="sf-other-topic" style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                Workshop / session topic <span style={{ color: 'var(--cr-error)' }}>*</span>
              </label>
              <input
                id="sf-other-topic"
                type="text"
                value={topic}
                onChange={(e) => { setTopic(e.target.value); setSavedAt(null); }}
                disabled={locked}
                maxLength={200}
                placeholder="e.g. Panel: Reaching Rural Communities"
                style={textInputStyle}
                required
              />
            </div>
            <div>
              <label htmlFor="sf-other-speaker" style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                Speaker (optional)
              </label>
              <input
                id="sf-other-speaker"
                type="text"
                value={speaker}
                onChange={(e) => { setSpeaker(e.target.value); setSavedAt(null); }}
                disabled={locked}
                maxLength={120}
                placeholder="Speaker's name"
                style={textInputStyle}
              />
            </div>
          </div>
        )}

        {questions.map((q, idx) => (
          <div key={q.key} style={{ marginBottom: '1.5rem' }}>
            <label htmlFor={`sf-${q.key}`} style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
              {idx + 1}. {q.text}
            </label>
            <textarea
              id={`sf-${q.key}`}
              value={answers[q.key] || ''}
              onChange={(e) => updateAnswer(q.key, e.target.value)}
              disabled={locked}
              spellCheck
              rows={4}
              maxLength={5000}
              placeholder="Type your answer…"
              style={textInputStyle}
            />
          </div>
        ))}

        {error && <p style={{ color: 'var(--cr-error)', marginBottom: '0.5rem' }}>{error}</p>}
        {savedAt && !error && (
          <p style={{ color: 'var(--cr-text-muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Saved {savedAt.toLocaleTimeString()}
          </p>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          {!locked && (
            <>
              <button type="button" className="btn-outline" onClick={() => save(false)} disabled={saving}>
                {saving ? 'Saving…' : 'Save Progress'}
              </button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Submitting…' : responseStatus === 'submitted' ? 'Update Submission' : 'Submit Feedback'}
              </button>
            </>
          )}
          <button type="button" className="btn-outline" onClick={() => navigate('/summit-feedback')}>
            {locked ? 'Back' : 'Cancel'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SummitFeedbackFormPage;
